import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Link,
  Typography,
  Table,
  TableCell,
  TableRow,
  TableBody,
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import SaveIcon from '@material-ui/icons/Save';
import { makeStyles } from '@material-ui/core/styles';
import { formatCurrency } from 'src/utils/currency';
import SecondaryChargeInfoDialog from 'src/components/PurchaseOrderSecondaryChargesDetails/modals/SecondaryChargeInfoDialog';

const useStyles = makeStyles((theme) => ({
  card: { 
    marginBottom: theme.spacing(2) 
  },
  cardHeader: {
    color: theme.palette.secondary.main,
  },
  cardContainer: { 
    backgroundColor: theme.palette.background.dark 
  },
  fontWeightMedium: { 
    fontWeight: theme.typography.fontWeightMedium 
  },
  fontWeightMediumTotal: { 
    fontWeight: theme.typography.fontWeightMedium, 
    float: 'right' 
  },
  cardTitle: {
    color: theme.palette.secondary.main,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    fontSize: theme.typography.h6.fontSize,
  },
  chargeTotal: {
    marginLeft: theme.spacing(6),
    color: theme.palette.common.black,
    [theme.breakpoints.down('xs')]: { display: 'none' },
    '& span': { color: theme.palette.primary.light },
  },
  chargeTotalDirty: {
    marginLeft: theme.spacing(6),
    fontWeight: theme.typography.fontWeightBold,
    color: `${theme.palette.warning.main} !important`,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  addButton: { 
    float: 'right', 
    top: '-2px',
  },
  addCreditCardFeeButton: {
    float: 'right',
    top: '-2px',
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  fontStyleHighlight: { 
    color: theme.palette.secondary.main, 
    fontWeight: 'bold',
  },
  chargeTotalStyle: { 
    marginRight: theme.spacing(2), 
    marginTop: theme.spacing(0.5), 
    float: 'right' 
  },
  saveButton: {
    position: 'relative',
    padding: theme.spacing(1, 3),
    float: 'right',
    backgroundColor: '#ffab00',
    '&:hover': { backgroundColor: '#b97706e8' },
  },
}));


function SecondaryChargeInfo(props) {
  const {
    chargeFees,
    chargeTypes,
    purchaseOrderDetails,
    setPurchaseOrderDetails,
    isReadOnly,
    showProviderCharges,
    setIsDirty,
    onSave,
  } = props;

  const classes = useStyles();
  const [availableCharges, setAvailableCharges] = useState(null);
  const [appliedCharges, setAppliedCharges] = useState(chargeFees || []);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isChargeReadOnly, setIsChargeReadOnly] = useState(false);
  const [isRateInfoModalDialogOpen, setIsRateInfoModalDialogOpen] = useState(false);

  const defaultCreditCardCharge = {
    purchaseOrderChargeId: null,
    poNumber: purchaseOrderDetails?.PoNumber || "",
    purchaseOrderChargeTypeId: "10", // Note: 10 is hardcoded for 'Credit Card Amount' which is the ID for the charge in Cash Qore DB
    purchaseOrderChargeTypeName: "CreditCardAmount",
    categoryId: "1",
    amount: 0,
    isCalculated: false,
    active: true,
  };

  useEffect(() => {
    if (appliedCharges?.length > 0 && purchaseOrderDetails?.CreditCardFeeTypeId === 2) {
      recalculateCreditCardFee();
    }
  }, [appliedCharges]);

  useEffect(() => {
    setAppliedCharges(prev => {
      if (prev.some(c => c.purchaseOrderChargeTypeId === 10)) return prev;

      return [
        ...prev,
        {
          ...defaultCreditCardCharge,
          poNumber: purchaseOrderDetails?.PoNumber || "",
        },
      ];
    });
  }, []);

  useEffect(() => {
    const availableCharges = chargeTypes == null ? [] :
      chargeTypes
        .filter(o => o.isInternalEditable)
        .map(o =>
          ({ ...o, formattedName: formatName(o.purchaseOrderChargeTypeName, 2), amount: 0 , categoryId: o?.categoryId}));

    setAvailableCharges(availableCharges);
  }, [chargeTypes]);

  const formatName = (name) =>
    name.replace(/([A-Z])/g, ' $1').trim();

  const filteredProviderCharges = () => 
    appliedCharges?.filter(o => o.categoryId === "1" && o.purchaseOrderChargeTypeId != 10 && o.amount > 0);

  const getSecondaryFeesTotal = () =>
    appliedCharges
      ?.filter(o => o.categoryId === "2" && Number(o.amount) > 0)
      .reduce((acc, curr) => acc + Number(curr.amount), 0);

  const getCreditCardCharge = () =>
    appliedCharges?.filter(o => o.purchaseOrderChargeTypeId == 10 && o.amount > 0);

  const hasCreditCardCharge = () =>
    appliedCharges?.some(o => o.purchaseOrderChargeTypeId == 10 && o.amount > 0);

  const recalculateCreditCardFee = () => {
    if (purchaseOrderDetails?.CreditCardFeeTypeId == 2) {
      const ccFeePercentage = purchaseOrderDetails?.CreditCardFeePercent || 0;
      if (getSecondaryFeesTotal() > 0 && ccFeePercentage > 0) {
        const ccPercentage = ccFeePercentage / 100; // Calculate the percentage first
        const rawSecondaryCCFeeAmount = getSecondaryFeesTotal() * ccPercentage; // Perform the multiplication
        const secondaryCCFeeAmount = Math.round(rawSecondaryCCFeeAmount * 100) / 100; // Round the result to two decimal places
        const ccAmount = appliedCharges?.filter(o => o.purchaseOrderChargeTypeId == 10);
        if (ccAmount && ccAmount.length > 0) {
          ccAmount[0].amount = secondaryCCFeeAmount;
          let poTotal = secondaryCCFeeAmount;
          appliedCharges.filter(o => o.purchaseOrderChargeTypeId != 10)
                        .forEach(o => poTotal += Number(o.amount ?? 0));
          setPurchaseOrderDetails(po => ({
            ...po,
            TotalPurchaseOrderAmount: poTotal
          }));
        }
      }
    }
  }

 const getCalculatedCreditCardFee = () => {
    const cc = appliedCharges?.find(o => o.purchaseOrderChargeTypeId == 10);
    return cc?.amount || 0;
  };

  function handleRateInfoModalApply(modifiedRateInfo) {
    const mutatedAppliedCharges = [...appliedCharges];
    purchaseOrderDetails.isDirty = true;
    setIsDirty(true);
    var existingCharges = mutatedAppliedCharges
      .filter(o => o.purchaseOrderChargeTypeId == selectedCharge.purchaseOrderChargeTypeId);

    var modifiedCharge = modifiedRateInfo
      .filter(o => o.purchaseOrderChargeTypeId == selectedCharge.purchaseOrderChargeTypeId)[0];
   
     if (!existingCharges || existingCharges.length == 0) {
      if (modifiedCharge.amount > 0) {
        mutatedAppliedCharges.push(modifiedCharge);
      }
    } else {
      if (modifiedCharge.amount > 0) {
        existingCharges[0].amount = Number(modifiedCharge.amount);
      } else {
        const index = mutatedAppliedCharges.indexOf(existingCharges[0]);
        mutatedAppliedCharges.splice(index, 1);
      }
    }
    modifiedRateInfo.forEach(o => {
      if (o.name == 'LoadedMileage') {
        purchaseOrderDetails.MilesLoaded = Number(o.amount);
      }
      if (o.name === 'LoadedFreeMiles' && purchaseOrderDetails?.ProviderCategory === 'Out Of Network') {
        purchaseOrderDetails.FreeLoadedMiles = Number(o.amount);
      }
      if (o.name === 'LoadedMileageRate' && purchaseOrderDetails?.ProviderCategory === 'Out Of Network') {
        purchaseOrderDetails.LoadedMileageRate = Number(o.amount);
      }
      if (o.name == 'UnloadedMileage') {
        purchaseOrderDetails.MilesUnloaded = Number(o.amount);
      }
      if (o.name === 'UnloadedFreeMiles' && purchaseOrderDetails?.ProviderCategory === 'Out Of Network') {
        purchaseOrderDetails.FreeUnloadedMiles = Number(o.amount);
      }
      if (o.name === 'UnloadedMileageRate' && purchaseOrderDetails?.ProviderCategory === 'Out Of Network') {
        purchaseOrderDetails.UnloadedMileageRate = Number(o.amount);
      }
      if (o.name == 'DailyStorageRate') {
        purchaseOrderDetails.StorageDailyRate = Number(o.amount);
      }
      if (o.name == 'DaysInStorage') {
        purchaseOrderDetails.StorageDays = Number(o.amount);
      }
      if (o.name == 'SecondaryDailyStorageRate') {
        purchaseOrderDetails.SecondaryStorageDailyRate = Number(o.amount);
      }
      if (o.name == 'SecondaryDaysInStorage') {
        purchaseOrderDetails.SecondaryStorageDays = Number(o.amount);
      }
      if (o.name == 'CreditCardFeePercent') {
        const ccFeePercentage = Number(o.amount);
        if (ccFeePercentage > 0) {
            purchaseOrderDetails.CreditCardFeePercent = ccFeePercentage;
            purchaseOrderDetails.CreditCardFeeTypeId = 2;
            const ccPercentage = ccFeePercentage / 100; // Calculate the percentage first
            const rawSecondaryCCFeeAmount = getSecondaryFeesTotal() * ccPercentage; // Perform the multiplication
            var existingCreditCardCharge = mutatedAppliedCharges.filter(o => o.purchaseOrderChargeTypeId == 10);
            existingCreditCardCharge[0].amount = rawSecondaryCCFeeAmount;
        }
      }
      if (o.name == 'CreditCardFeeAmount') {
        var existingCreditCardCharge = mutatedAppliedCharges.filter(o => o.purchaseOrderChargeTypeId == 10);
        if (existingCreditCardCharge && existingCreditCardCharge.length > 0) {
          const ccFeeAmount = Number(o.amount);
          if (ccFeeAmount > 0) {
            existingCreditCardCharge[0].amount = ccFeeAmount;
            purchaseOrderDetails.CreditCardFeePercent = 0;
            purchaseOrderDetails.CreditCardFeeTypeId = 1;
         }
       }
      }
    });

    let poTotal = 0;
    mutatedAppliedCharges.forEach(o => poTotal += Number(o.amount ?? 0));
    purchaseOrderDetails.TotalPurchaseOrderAmount = poTotal;
    purchaseOrderDetails.Charges = mutatedAppliedCharges;
    setPurchaseOrderDetails({ ...purchaseOrderDetails });
    setAppliedCharges(mutatedAppliedCharges);
    recalculateCreditCardFee();
    setIsRateInfoModalDialogOpen(false);
    setSelectedCharge(null);
  }

  function handleAddRateInfo(charge) {
    setIsChargeReadOnly(!charge);
    if (charge) {
      const matchingChargeTypes = availableCharges
        .filter(o => o.purchaseOrderChargeTypeId == charge.purchaseOrderChargeTypeId);
      setSelectedCharge(
        matchingChargeTypes.length > 0 ?
          matchingChargeTypes[0] : null);
    } else {
      setSelectedCharge(null);
    }

    setIsRateInfoModalDialogOpen(true);
  }

  function handleAddCreditCardCharge() {
    const existing = appliedCharges.find(
      o => o.purchaseOrderChargeTypeId === 10
    );

    let updatedCharges = [...appliedCharges];
    let selected;
    if (!existing) {
      const newCharge = { ...defaultCreditCardCharge };
      updatedCharges.push(newCharge);
      selected = newCharge;
    } else {
      selected = existing;
    }

    setAppliedCharges(updatedCharges);
    setSelectedCharge(selected);
    setIsRateInfoModalDialogOpen(true);
  }

  const getDisplayCreditCardFeeType = () =>
    purchaseOrderDetails?.CreditCardFeeTypeId === 2
      ? `Percentage (${purchaseOrderDetails?.CreditCardFeePercent}%)`
      : 'Fixed';

  const handleSavePurchaseOrder = () => {
    setPurchaseOrderDetails(purchaseOrderDetails);
    if (onSave) {
      onSave(purchaseOrderDetails);
    }
  }

  return (
    <>
      {(availableCharges) &&
        <SecondaryChargeInfoDialog
          isDialogOpen={isRateInfoModalDialogOpen}
          setIsDialogOpen={setIsRateInfoModalDialogOpen}
          handleApply={handleRateInfoModalApply}
          availableCharges={availableCharges}
          appliedCharges={appliedCharges}
          selectedCharge={selectedCharge}
          setSelectedCharge={setSelectedCharge}
          purchaseOrderDetails={purchaseOrderDetails}
          isChargeReadOnly={isChargeReadOnly}
          providerCategory={purchaseOrderDetails?.ProviderCategory}
          showProviderCharges={showProviderCharges}
        />}
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardTitle}
            title={(
              <>
                <Button
                  className={classes.addButton}
                  variant='contained'
                  color='primary'
                  onClick={() => handleAddRateInfo(null, 1)}
                  disabled={isReadOnly}
                >
                  Add a Charge
                </Button>
              </>)}
          />
          <Divider />
          {showProviderCharges && (
          <>
            <Grid container style={{ marginTop: '16px' }} xs={12}>
              <Grid item xs={8}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.fontStyleHighlight}
                  style={{ marginLeft: '16px' }}
                >
                  Provider Charges
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.chargeTotalStyle}
                >
                  Total:&nbsp;
                  <span className={classes.fontStyleHighlight}>
                    {formatCurrency(
                      (appliedCharges || [])
                        .filter(o => o.categoryId === "1" && o.purchaseOrderChargeTypeId != 10 && Number(o.amount) > 0)
                        .reduce((acc, curr) => acc + Number(curr.amount || 0), 0)
                    )}
                  </span>
                </Typography>
              </Grid>
            </Grid>

            {filteredProviderCharges()?.length > 0 && (
              <Grid container xs={12}>
                {filteredProviderCharges().map(o => (
                  <Grid item key={o.purchaseOrderChargeTypeName} xs={12}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.fontWeightMedium}>
                            {formatName(o.purchaseOrderChargeTypeName)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrency(o.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

         <Grid container style={{marginTop: '16px'}} xs={12}>
            <Grid xs={8} >
              <Typography variant="body2" color="textSecondary" className={classes.fontStyleHighlight} style={{ marginLeft: '16px', marginTop: '8px' }}>
                Secondary Charges
              </Typography>
            </Grid>
            <Grid xs={4}>
              <Typography variant="body2" color="textSecondary" className={classes.chargeTotalStyle}>
                 Total: &nbsp;
                <span className={classes.fontStyleHighlight}>
                  {formatCurrency(getSecondaryFeesTotal())}
                </span>
              </Typography>
            </Grid>
          </Grid>
          <Grid container xs={12}>
          {(appliedCharges) && appliedCharges.filter(o => o.categoryId === "2" && o.amount > 0).map(o => {
              return (
                <Grid item key={o.purchaseOrderChargeTypeName}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.fontWeightMedium}>
                          {formatName(o.purchaseOrderChargeTypeName)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            <Link
                              component='button'
                              disabled={isReadOnly}
                              style={{
                                cursor: isReadOnly ? 'default' : 'pointer'
                              }}
                              onClick={() => handleAddRateInfo(o, 2)}
                            >
                              {formatCurrency(o.amount)}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              );
            })}
          </Grid>
        </Card> 

        <Grid container style={{ marginTop: '16px' }} xs={12}>
          <Grid item xs={12}>
            <Button
                className={classes.addCreditCardFeeButton}
                variant='contained'
                color='primary'
                onClick={() => handleAddCreditCardCharge()}
                disabled={isReadOnly}
                startIcon={<CreditCardIcon />}
                >
              {hasCreditCardCharge() ? 'Edit Credit Card Fee' : 'Add Credit Card Fee'}
            </Button>
          </Grid>
              <Grid item xs={8}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.fontStyleHighlight}
                  style={{ marginLeft: '16px' }}
                >
                  Credit Card Fee Details
                </Typography>
              </Grid>
              <Grid item xs={4}>
               
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.chargeTotalStyle}
                >
                  CC Fee:&nbsp;
                  <span className={classes.fontStyleHighlight}>
                    {formatCurrency(
                      getCalculatedCreditCardFee()
                    )}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={12}>
              <Divider />
              <Card>
              
                { hasCreditCardCharge() && getCreditCardCharge().map(o => (
                  <Grid item key={o.purchaseOrderChargeTypeName} xs={12}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.fontWeightMedium}>
                            Credit Card Fee*
                          </TableCell>
                          <TableCell className={classes.fontWeightMedium}>
                            Type: {getDisplayCreditCardFeeType()}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                              <Link
                                component="button"
                                style={{ cursor: 'default' }}
                                onClick={() => handleAddCreditCardCharge()}
                              >
                                {formatCurrency(o.amount)}
                              </Link>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                ))}
                </Card>
                <Card>
                  <Grid item key="po_estimated_total" xs={12}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell />
                          <TableCell />
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                                 Total Estimated Amount:
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              <span className={purchaseOrderDetails?.isDirty == true ? classes.chargeTotalDirty : classes.chargeTotal}>
                                {formatCurrency(purchaseOrderDetails?.TotalPurchaseOrderAmount ?? 0)}
                              </span>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                      <Divider />
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={2} className={classes.fontWeightMedium}>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                            </Typography>
                            {!isReadOnly && (
                              <Button className={classes.saveButton}
                                startIcon={<SaveIcon />}
                                variant='contained' color='primary'
                                onClick={() => handleSavePurchaseOrder()}>
                                Save Changes
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </div>
        </>
  );
};

SecondaryChargeInfo.propTypes = {
  chargeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      purchaseOrderChargeTypeId: PropTypes.string,
      purchaseOrderChargeTypeName: PropTypes.string,
      categoryId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      isCalculated: PropTypes.bool,
      isExternalEditable: PropTypes.bool,
      isInternalEditable: PropTypes.bool,
    })
  ),
  chargeFees: PropTypes.arrayOf(
    PropTypes.shape({
      purchaseOrderChargeId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      poNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      purchaseOrderChargeTypeId: PropTypes.string,
      purchaseOrderChargeTypeName: PropTypes.string,
      categoryId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      amount: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]),
      isCalculated: PropTypes.bool,
      isExternalEditable: PropTypes.bool,
      isInternalEditable: PropTypes.bool,
    })
  ),
  purchaseOrderDetails: PropTypes.object.isRequired,
  setPurchaseOrderDetails: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  setIsDirty: PropTypes.func,
  showProviderCharges: PropTypes.bool,
  onSave: PropTypes.func,
};

SecondaryChargeInfo.defaultProps = {
  isReadOnly: false,
  showProviderCharges: false,
};

export default SecondaryChargeInfo;
